/**
 * Lock Management Component
 *
 * Displays and manages sync locks from the sync_locks table.
 * Allows admins to view stuck locks (stale heartbeat) and release them when necessary.
 *
 * Note: Advisory locks removed in favor of table-based locking with heartbeat detection.
 */

import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Button,
  Chip,
  Alert,
  Stack,
  CircularProgress,
  Collapse,
  keyframes
} from '@mui/material'
import {
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  Refresh as RefreshIcon,
  CleaningServices as CleanupIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon
} from '@mui/icons-material'
import {
  AdvisoryLock,
  LockListResponse,
  syncApi,
  handleApiError
} from '../services/api'
import { poolwerxColors } from '../theme/colors'
import ConfirmDialog from './shared/dialogs/ConfirmDialog'

// Keyframe animation for pulsing button
const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`

interface LockManagementProps {
  onLockReleased?: () => void
  refreshInterval?: number
}

export const LockManagement: React.FC<LockManagementProps> = ({
  onLockReleased,
  refreshInterval = 10000 // 10 seconds
}) => {
  const [locks, setLocks] = useState<AdvisoryLock[]>([])
  const [syncLocks, setSyncLocks] = useState<AdvisoryLock[]>([])
  const [stuckLocks, setStuckLocks] = useState<AdvisoryLock[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Dialog states
  const [releaseDialog, setReleaseDialog] = useState<{
    open: boolean
    lock: AdvisoryLock | null
  }>({ open: false, lock: null })
  
  const [cleanupDialog, setCleanupDialog] = useState(false)
  const [isReleasing, setIsReleasing] = useState<number[]>([])
  const [isCleaningUp, setIsCleaningUp] = useState(false)

  // Load locks data
  const loadLocks = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setIsLoading(true)
      }
      setError(null)

      const response = await syncApi.getLocks()
      const data: LockListResponse = response.data

      setLocks(data.locks)
      setSyncLocks(data.sync_locks)
      setStuckLocks(data.stuck_locks)

    } catch (error) {
      console.error('Failed to load locks:', error)
      setError(handleApiError(error))
      setLocks([])
      setSyncLocks([])
      setStuckLocks([])
    } finally {
      if (showLoading) {
        setIsLoading(false)
      }
    }
  }, [])

  // Auto-refresh locks
  useEffect(() => {
    loadLocks()
    
    const interval = setInterval(() => {
      if (isExpanded) {
        loadLocks(false)
      }
    }, refreshInterval)
    
    return () => clearInterval(interval)
  }, [loadLocks, refreshInterval, isExpanded])

  // Handle individual lock release
  const handleReleaseLock = async (lock: AdvisoryLock) => {
    if (!lock.fp_code || !lock.system) {
      console.error('Lock missing fp_code or system:', lock)
      return
    }

    try {
      setIsReleasing(prev => [...prev, lock.lock_hash])

      await syncApi.releaseLock(
        lock.fp_code,
        lock.system,
        `Manual release of stale lock (Heartbeat Age: ${lock.heartbeat_age_minutes?.toFixed(1)}min, Worker: ${lock.worker_id || 'unknown'})`
      )

      // Refresh locks after release
      await loadLocks(false)

      // Notify parent component
      onLockReleased?.()

      setReleaseDialog({ open: false, lock: null })

    } catch (error) {
      console.error('Failed to release lock:', error)
      setError(handleApiError(error))
    } finally {
      setIsReleasing(prev => prev.filter(hash => hash !== lock.lock_hash))
    }
  }

  // Handle bulk cleanup
  const handleCleanup = async () => {
    try {
      setIsCleaningUp(true)
      setCleanupDialog(false)
      
      const response = await syncApi.cleanupLocks()
      
      // Refresh locks after cleanup
      await loadLocks(false)
      
      // Notify parent component
      onLockReleased?.()
      
      console.log('Lock cleanup completed:', response.data)
      
    } catch (error) {
      console.error('Failed to cleanup locks:', error)
      setError(handleApiError(error))
    } finally {
      setIsCleaningUp(false)
    }
  }

  // Get lock status info
  const getLockStatus = (lock: AdvisoryLock) => {
    if (lock.appears_stuck) {
      return {
        color: poolwerxColors.error,
        icon: '🔴',
        label: 'Stale',
        severity: 'error' as const
      }
    } else if (lock.heartbeat_age_minutes && lock.heartbeat_age_minutes > 2) {
      return {
        color: poolwerxColors.warning,
        icon: '🟡',
        label: 'Aging',
        severity: 'warning' as const
      }
    } else {
      return {
        color: poolwerxColors.success,
        icon: '🟢',
        label: 'Active',
        severity: 'success' as const
      }
    }
  }

  const formatAge = (minutes?: number) => {
    if (!minutes) return 'Unknown'
    if (minutes < 1) return '< 1min'
    if (minutes < 60) return `${minutes.toFixed(0)}min`
    const hours = Math.floor(minutes / 60)
    const mins = Math.floor(minutes % 60)
    return `${hours}h ${mins}min`
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockIcon />
              Sync Locks
              {syncLocks.length > 0 && (
                <Chip
                  label={`${syncLocks.length} active`}
                  size="small"
                  color={stuckLocks.length > 0 ? 'error' : 'primary'}
                />
              )}
            </Typography>

            {stuckLocks.length > 0 && (
              <Alert
                severity="error"
                sx={{ py: 0.5, px: 2, fontWeight: 'bold', border: '2px solid', borderColor: 'error.main' }}
                icon={<WarningIcon fontSize="small" />}
              >
                🚨 {stuckLocks.length} stale lock{stuckLocks.length !== 1 ? 's' : ''} (heartbeat expired)!
              </Alert>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {stuckLocks.length > 0 && (
              <Button
                size="medium"
                variant="contained"
                color="error"
                startIcon={<CleanupIcon />}
                onClick={() => setCleanupDialog(true)}
                disabled={isCleaningUp}
                sx={{ 
                  fontWeight: 'bold',
                  boxShadow: 3,
                  '&:hover': {
                    boxShadow: 6
                  },
                  animation: `${pulse} 2s infinite`
                }}
              >
                🧹 Fix Stuck Locks
              </Button>
            )}
            
            {syncLocks.length === 0 && stuckLocks.length === 0 && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<CleanupIcon />}
                onClick={() => setCleanupDialog(true)}
                disabled={isCleaningUp}
              >
                Cleanup
              </Button>
            )}
            
            <IconButton
              onClick={() => loadLocks()}
              disabled={isLoading}
              size="small"
            >
              {isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
            
            <IconButton
              onClick={() => setIsExpanded(!isExpanded)}
              size="small"
            >
              {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Expandable Content */}
        <Collapse in={isExpanded}>
          {/* Summary Stats */}
          <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              label={`${locks.length} Total Locks`} 
              variant="outlined" 
            />
            <Chip 
              label={`${syncLocks.length} Sync Locks`} 
              color="primary"
              variant="outlined"
            />
            {stuckLocks.length > 0 && (
              <Chip 
                label={`${stuckLocks.length} Stuck`} 
                color="error"
              />
            )}
          </Box>

          {/* Locks Table */}
          {syncLocks.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Franchise</TableCell>
                    <TableCell>System</TableCell>
                    <TableCell>Worker</TableCell>
                    <TableCell>Heartbeat Age</TableCell>
                    <TableCell>Total Age</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {syncLocks.map((lock) => {
                    const status = getLockStatus(lock)
                    const isReleaseInProgress = isReleasing.includes(lock.lock_hash)

                    return (
                      <TableRow
                        key={lock.lock_hash}
                        sx={{
                          backgroundColor: lock.appears_stuck
                            ? `${poolwerxColors.error}08`
                            : undefined
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={status.label}
                            size="small"
                            sx={{
                              backgroundColor: `${status.color}20`,
                              color: status.color,
                              fontWeight: 'bold'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {lock.fp_code || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {lock.system || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                            {lock.worker_id ? lock.worker_id.split('-').slice(0, 3).join('-') : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color={lock.appears_stuck ? 'error' : 'textSecondary'}
                            fontWeight={lock.appears_stuck ? 'medium' : 'normal'}
                          >
                            {formatAge(lock.heartbeat_age_minutes)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {formatAge(lock.age_minutes)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {lock.appears_stuck && lock.fp_code && lock.system && (
                            <Tooltip title="Release stale lock">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setReleaseDialog({ open: true, lock })}
                                disabled={isReleaseInProgress}
                              >
                                {isReleaseInProgress ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <UnlockIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="success" icon="✅">
              No active sync locks. All syncs have completed.
            </Alert>
          )}
        </Collapse>

        {/* Loading overlay for cleanup */}
        {isCleaningUp && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography variant="body2" color="textSecondary">
              Cleaning up stale locks...
            </Typography>
          </Box>
        )}
      </CardContent>

      {/* Release Lock Confirmation Dialog */}
      <ConfirmDialog
        open={releaseDialog.open}
        onClose={() => setReleaseDialog({ open: false, lock: null })}
        onConfirm={() => {
          if (releaseDialog.lock) void handleReleaseLock(releaseDialog.lock)
        }}
        tone="danger"
        title="Release Sync Lock"
        subtitle={
          releaseDialog.lock
            ? `${releaseDialog.lock.fp_code}:${releaseDialog.lock.system}`
            : undefined
        }
        confirmLabel="Release Lock"
        pending={
          releaseDialog.lock ? isReleasing.includes(releaseDialog.lock.lock_hash) : false
        }
        body={
          <Stack spacing={1.5}>
            <Typography variant="body2">
              Are you sure you want to release the sync lock for{' '}
              <strong>{releaseDialog.lock?.fp_code}:{releaseDialog.lock?.system}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Worker: {releaseDialog.lock?.worker_id || 'Unknown'}<br />
              • Heartbeat Age: {formatAge(releaseDialog.lock?.heartbeat_age_minutes)}<br />
              • Total Age: {formatAge(releaseDialog.lock?.age_minutes)}
            </Typography>
            <Alert severity="warning">
              Only release locks if you're certain the sync is not actually running.
            </Alert>
          </Stack>
        }
      />

      {/* Cleanup Confirmation Dialog */}
      <ConfirmDialog
        open={cleanupDialog}
        onClose={() => setCleanupDialog(false)}
        onConfirm={handleCleanup}
        tone={stuckLocks.length > 0 ? 'danger' : 'warning'}
        title={stuckLocks.length > 0 ? 'Fix Stale Sync Locks' : 'Cleanup Sync Locks'}
        confirmLabel={stuckLocks.length > 0 ? 'Fix Now' : 'Cleanup Locks'}
        body={
          stuckLocks.length > 0 ? (
            <Stack spacing={1.5}>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                Found {stuckLocks.length} stale sync lock
                {stuckLocks.length !== 1 ? 's' : ''} with expired heartbeats.
              </Typography>
              <Alert severity="warning">
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Stale locks may prevent new syncs from starting
                </Typography>
                <Typography variant="body2">
                  This cleanup will remove lock entries with heartbeats older than 5 minutes.
                </Typography>
              </Alert>
              <Box sx={{ backgroundColor: 'grey.100', p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  What will be cleaned up:
                </Typography>
                <Typography variant="body2">
                  • Remove sync_locks entries with stale heartbeats
                </Typography>
                <Typography variant="body2">• Allow blocked syncs to start fresh</Typography>
              </Box>
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <Typography variant="body2">
                This will perform routine cleanup of any stale sync lock entries.
              </Typography>
              <Alert severity="info">
                This action only removes locks with expired heartbeats.
              </Alert>
            </Stack>
          )
        }
      />
    </Card>
  )
}

export default LockManagement